/**
 * Geometry for the seat map's gesture frames.
 *
 * While a pan, pinch, wheel zoom or zoom animation moves a map that is slow to
 * paint on the device (a phone at the overview of thousands of seats), the map
 * does not repaint every section, seat and overview dot each frame: it draws
 * offscreen bitmaps of those static layers — the view with a margin round it,
 * and the whole map at the overview behind it — moved and scaled to the
 * current view, with the section labels drawn live on top. A bitmap that is
 * about to stop fitting the gesture is repainted a band per frame, so no frame
 * waits on a whole paint. When the gesture ends the map is painted normally
 * again, so a map at rest is exactly what it always was.
 *
 * This module is the arithmetic only — which part of the map the bitmap
 * holds, at what resolution, whether it still covers the view, and where it
 * lands on screen — so it can be tested without a canvas.
 *
 * Coordinates: a view transform `t` puts world point p at screen CSS px
 * p * t.scale + (t.x, t.y). A bitmap's own transform puts p at ITS CSS px the
 * same way; its backing store has `dpr` device px per CSS px.
 */

export interface ViewTransform {
  x: number
  y: number
  scale: number
}

/** Axis-aligned box: world units, or CSS px where noted. */
export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export interface GestureCachePlan {
  /** The part of the view's CSS space the bitmap holds (whole CSS px; may extend past the view). */
  region: Box
  /** Bitmap device px per CSS px. */
  dpr: number
  /** Bitmap backing size in device px. */
  width: number
  height: number
  /** The transform to paint the bitmap with: the view's, shifted to the region's origin. */
  transform: ViewTransform
  /** Map held past each edge of the view, in CSS px (where there is map). */
  margin: number
}

export const GESTURE_CACHE_LIMITS = {
  /** Map kept around the view on every side, as a share of the view's longer side: room
   *  to pan and zoom out (the same distance across a portrait phone as along it). */
  margin: 0.5,
  /** Smallest margin accepted before trading resolution for it. */
  minMargin: 0.2,
  /** Default extra resolution over the screen's, when it fits: room to zoom in before it
   *  softens. */
  supersample: 2,
  /** Bitmap size cap in device px (6 MP = 24 MB). Up to three are alive during a gesture
   *  (overview, main, next) plus the view canvas; WebKit caps a canvas at ~16.7 MP and
   *  limits the page's total canvas memory, and low-memory phones need the headroom. */
  maxPixels: 6_000_000,
  /** Longest side in device px; larger canvases fall off the GPU on many Android phones. */
  maxSide: 4096,
  /** Screen CSS px painted beyond the map's own box (shadows, strokes, seat edges). */
  pad: 24,
  /** Repaint once the bitmap is magnified so far that it holds less than this many
   *  device px per screen device px... */
  minSharpness: 0.55,
  /** ...but always allow at least this much zoom in before repainting, and never more than
   *  this. */
  minZoomIn: 1.6,
  maxZoomIn: 2.5,
  /** Repaint once zoomed out this far: the bitmap shows another zoom level's detail. */
  maxZoomOut: 0.25,
}

function intersect(a: Box, b: Box): Box | null {
  const x0 = Math.max(a.x, b.x), y0 = Math.max(a.y, b.y)
  const x1 = Math.min(a.x + a.w, b.x + b.w), y1 = Math.min(a.y + a.h, b.y + b.h)
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null
}

export interface GesturePlanOptions {
  /** Extra resolution over the screen's (default `limits.supersample`). */
  supersample?: number
  /** Margin round the view as a share of its longer side (default `limits.margin`);
   *  never shrunk below this when it is given. */
  margin?: number
}

/**
 * What to hold in the bitmap for a view of `viewW` x `viewH` CSS px at `t`:
 * the view plus a margin on every side, clipped to the map's own box
 * (`paintBox`, world units, plus a few px for shadows) — at the overview the
 * whole map fits, so the bitmap is the map. Resolution is the screen's
 * (`dpr`) with supersampling headroom when it fits in the pixel budget; the
 * margin shrinks before the resolution drops below the screen's. Null when
 * no part of the map is in or near the view.
 */
export function planGestureCache(
  viewW: number,
  viewH: number,
  t: ViewTransform,
  paintBox: Box,
  dpr: number,
  opts: GesturePlanOptions = {},
  limits = GESTURE_CACHE_LIMITS,
): GestureCachePlan | null {
  const supersample = opts.supersample ?? limits.supersample
  if (!(viewW > 0 && viewH > 0 && t.scale > 0 && dpr > 0)) return null
  const pad = limits.pad
  const mapCss: Box = {
    x: paintBox.x * t.scale + t.x - pad,
    y: paintBox.y * t.scale + t.y - pad,
    w: paintBox.w * t.scale + pad * 2,
    h: paintBox.h * t.scale + pad * 2,
  }
  const regionFor = (margin: number): Box | null => {
    const m = Math.max(viewW, viewH) * margin
    const r = intersect({ x: -m, y: -m, w: viewW + m * 2, h: viewH + m * 2 }, mapCss)
    if (!r) return null
    // Whole CSS px, rounded outward: at 1:1 the bitmap then lands on the
    // screen's own pixel grid.
    const x0 = Math.floor(r.x) || 0, y0 = Math.floor(r.y) || 0 // (never -0)
    return { x: x0, y: y0, w: Math.ceil(r.x + r.w) - x0, h: Math.ceil(r.y + r.h) - y0 }
  }
  const resolutionFor = (r: Box) => Math.min(
    dpr * Math.max(1, supersample),
    Math.sqrt(limits.maxPixels / (r.w * r.h)),
    limits.maxSide / r.w,
    limits.maxSide / r.h,
  )
  let margin = opts.margin ?? limits.margin
  let region = regionFor(margin)
  if (!region) return null
  let res = resolutionFor(region)
  // Resolution before margin: a bitmap softer than the screen shows as soon
  // as the gesture starts; a smaller margin only matters after a long pan.
  const minMargin = Math.min(margin, limits.minMargin)
  for (let m = margin - 0.1; res < dpr && m >= minMargin - 1e-9; m -= 0.1) {
    const smaller = regionFor(m)
    if (!smaller) break
    region = smaller
    margin = m
    res = resolutionFor(region)
  }
  const width = Math.max(1, Math.round(region.w * res))
  const height = Math.max(1, Math.round(region.h * res))
  return {
    region,
    dpr: res,
    width,
    height,
    transform: { x: t.x - region.x, y: t.y - region.y, scale: t.scale },
    margin: Math.max(viewW, viewH) * margin,
  }
}

export interface GestureCacheView {
  /** The transform the bitmap was painted with (its own CSS px). */
  transform: ViewTransform
  /** Bitmap size in its CSS px (backing size / dpr). */
  cssW: number
  cssH: number
  /** Bitmap device px per CSS px. */
  dpr: number
  /** Map held past each edge of the view it was laid out for, in CSS px. */
  margin?: number
}

/**
 * Whether the bitmap holds every part of the map the view at `t` shows
 * (anything else would show as blank). `aheadPx` widens the view by that
 * many CSS px on every side: "will it still, a little further on?"
 */
export function gestureCacheCovers(
  cache: GestureCacheView,
  t: ViewTransform,
  viewW: number,
  viewH: number,
  paintBox: Box,
  aheadPx = 0,
): boolean {
  const a = aheadPx
  const visible = intersect({ x: (-a - t.x) / t.scale, y: (-a - t.y) / t.scale, w: (viewW + a * 2) / t.scale, h: (viewH + a * 2) / t.scale }, paintBox)
  if (!visible) return true
  const cs = cache.transform.scale
  const held: Box = { x: -cache.transform.x / cs, y: -cache.transform.y / cs, w: cache.cssW / cs, h: cache.cssH / cs }
  // Half a screen px of slack for rounding.
  const eps = 0.5 / t.scale
  return visible.x >= held.x - eps && visible.y >= held.y - eps &&
    visible.x + visible.w <= held.x + held.w + eps && visible.y + visible.h <= held.y + held.h + eps
}

/** Whether the bitmap holds all of `box` (world units): e.g. the whole map. */
export function gestureCacheHolds(cache: GestureCacheView, box: Box): boolean {
  const cs = cache.transform.scale
  const x = -cache.transform.x / cs, y = -cache.transform.y / cs
  return box.x >= x && box.y >= y && box.x + box.w <= x + cache.cssW / cs && box.y + box.h <= y + cache.cssH / cs
}

/**
 * Whether the bitmap can stand in for the view at `t`: it covers the view
 * (see gestureCacheCovers) and is neither magnified past its sharpness nor
 * shrunk past another zoom level. With look-ahead the answer is for a view a
 * little further on: `ahead.px` CSS px wider each side, `ahead.zoom` (a
 * share) further zoomed either way.
 */
export function gestureCacheUsable(
  cache: GestureCacheView,
  t: ViewTransform,
  viewW: number,
  viewH: number,
  paintBox: Box,
  screenDpr: number,
  ahead: { px?: number; zoom?: number } = {},
  limits = GESTURE_CACHE_LIMITS,
): boolean {
  const ratio = t.scale / cache.transform.scale
  if (!(ratio > 0)) return false
  const zoomInLimit = Math.min(limits.maxZoomIn, Math.max(limits.minZoomIn, cache.dpr / (screenDpr * limits.minSharpness)))
  const z = 1 + (ahead.zoom ?? 0)
  if (ratio * z > zoomInLimit || ratio / z < limits.maxZoomOut) return false
  return gestureCacheCovers(cache, t, viewW, viewH, paintBox, ahead.px ?? 0)
}

/**
 * Splitting a bitmap's paint over several frames: how many device rows the
 * next step should paint so it takes about `budgetMs`, given that the last
 * step painted `rows` rows in `tookMs`.
 */
export function nextBandRows(rows: number, tookMs: number, budgetMs: number, totalRows: number): number {
  const minRows = Math.min(totalRows, 32)
  if (!(tookMs > 0.05)) return Math.min(totalRows, Math.max(minRows, rows * 2))
  const scaled = Math.round(rows * Math.min(2, budgetMs / tookMs))
  return Math.min(totalRows, Math.max(minRows, scaled))
}

/** Where the bitmap lands on the view's canvas at `t`, in the canvas's device px. */
export function gestureCacheDest(cache: GestureCacheView, t: ViewTransform, screenDpr: number): Box {
  const r = t.scale / cache.transform.scale
  return {
    x: (t.x - cache.transform.x * r) * screenDpr,
    y: (t.y - cache.transform.y * r) * screenDpr,
    w: cache.cssW * r * screenDpr,
    h: cache.cssH * r * screenDpr,
  }
}

/** Same inputs, element by element (identity, as React compares dependencies). */
export function sameInputs(a: readonly unknown[] | null | undefined, b: readonly unknown[]): boolean {
  if (!a || a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false
  return true
}
